import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { 
  MessageCircle, 
  X, 
  Send, 
  Bot, 
  User, 
  Plus, 
  Search, 
  Edit,
  Lightbulb
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { createLead, updateLead, fetchLeads } from '@/services/leadsService';
import { Lead, LeadStatus, LeadPriority, STATUS_LABELS } from '@/components/lead-card';
import { logger } from '@/lib/logger';

interface ChatMessage {
  id: string;
  content: string;
  type: 'user' | 'bot';
  timestamp: Date;
  leadData?: Partial<Lead>;
  action?: 'create' | 'update' | 'search' | 'help';
}

interface LeadCreationState {
  isCreating: boolean;
  currentStep: 'firstName' | 'lastName' | 'notes' | 'complete';
  leadData: Partial<Lead>;
}

interface LeadSearchState {
  isSearching: boolean;
  currentStep: 'firstName' | 'lastName' | 'phone' | 'search';
  searchData: {
    firstName?: string;
    lastName?: string;
    phone?: string;
  };
}

interface LeadEditState {
  isEditing: boolean;
  currentStep: 'action' | 'status' | 'notes' | 'contact';
  leadToEdit: Lead | null;
  editData: {
    status?: LeadStatus;
    notes?: string;
    nextContactDate?: string;
  };
}

interface ChatbotProps {
  onLeadCreated?: (lead: Lead) => void;
  onLeadUpdated?: (lead: Lead) => void;
}

export function Chatbot({ onLeadCreated, onLeadUpdated }: ChatbotProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [leadCreation, setLeadCreation] = useState<LeadCreationState>({
    isCreating: false,
    currentStep: 'firstName',
    leadData: {}
  });
  const [leadSearch, setLeadSearch] = useState<LeadSearchState>({
    isSearching: false,
    currentStep: 'firstName',
    searchData: {}
  });
  const [leadEdit, setLeadEdit] = useState<LeadEditState>({
    isEditing: false,
    currentStep: 'action',
    leadToEdit: null,
    editData: {}
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { currentUser } = useAuth();

  // Only show chatbot for admins and developers
  if (!currentUser || (currentUser.role !== 'admin' && currentUser.role !== 'developer')) {
    return null;
  }

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Focus input when chatbot opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Initialize with welcome message
  useEffect(() => {
    if (messages.length === 0) {
      const welcomeMessage: ChatMessage = {
        id: 'welcome',
        content: `Hallo! 👋 Ich bin dein Lead-Assistent.

Verwende die Schnellaktionen unten für:

🆕 **Lead erstellen** - Schritt-für-Schritt Anleitung
🔍 **Lead suchen** - Nach Name oder Telefon
📅 **Kalender** - Heutige Termine & Übersicht  
📊 **Statistik** - Deine Lead-Zahlen

Du kannst auch einfach schreiben, was du möchtest!`,
        type: 'bot',
        timestamp: new Date(),
        action: 'help'
      };
      setMessages([welcomeMessage]);
    }
  }, []);

  const addMessage = (content: string, type: 'user' | 'bot', action?: string, leadData?: Partial<Lead>) => {
    const newMessage: ChatMessage = {
      id: Date.now().toString(),
      content,
      type,
      timestamp: new Date(),
      leadData,
      action: action as ChatMessage['action']
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const resetWorkflows = () => {
    setLeadCreation({ isCreating: false, currentStep: 'firstName', leadData: {} });
    setLeadSearch({ isSearching: false, currentStep: 'firstName', searchData: {} });
    setLeadEdit({ isEditing: false, currentStep: 'action', leadToEdit: null, editData: {} });
  };

  const parseLeadFromText = (text: string): Partial<Lead> | null => {
    logger.log('Parsing lead from text:', text);
    
    const leadData: Partial<Lead> = {};
    
    // Extract name patterns - improved to handle more cases
    const namePatterns = [
      /(?:für|name|lead)\s+([A-ZÄÖÜ][a-zäöüß]*)\s+([A-ZÄÖÜ][a-zäöüß]*)/i,
      /(?:für|name|lead)\s+([A-ZÄÖÜ][a-zäöüß]*)/i, // Single name after "für"
      /([A-ZÄÖÜ][a-zäöüß]*)\s+([A-ZÄÖÜ][a-zäöüß]*)(?:\s|,|$)/, // Two capitalized words
      /([A-Za-zÄÖÜäöüß]+)\s+([A-Za-zÄÖÜäöüß]+)(?:\s|,|$)/ // Any two words
    ];
    
    for (const pattern of namePatterns) {
      const nameMatch = text.match(pattern);
      if (nameMatch) {
        if (nameMatch[2]) {
          // Two names found
          leadData.firstName = nameMatch[1];
          leadData.lastName = nameMatch[2];
        } else {
          // Only one name found, use as firstName and prompt for lastName
          leadData.firstName = nameMatch[1];
          leadData.lastName = ""; // Will be handled later
        }
        break;
      }
    }
    
    // Extract email
    const emailMatch = text.match(/(?:email|mail|e-mail)[\s:,]*([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/i);
    if (emailMatch) {
      leadData.email = emailMatch[1];
    }
    
    // Extract phone  
    const phonePatterns = [
      /(?:telefon|phone|tel)[\s:,]*([+\d\s-()]{6,})/i,
      /(\+?\d{2,4}[\s-]?\d{3,4}[\s-]?\d{3,8})/, // Phone number pattern
      /(\d{3,4}[\s-]?\d{3,8})/ // Simple phone pattern
    ];
    
    for (const pattern of phonePatterns) {
      const phoneMatch = text.match(pattern);
      if (phoneMatch) {
        leadData.phone = phoneMatch[1].replace(/\s+/g, '').replace(/[^\d+]/g, '');
        break;
      }
    }
    
    // Extract status
    const statusKeywords = {
      'neu': 'new',
      'kontaktiert': 'not-reached', 
      'terminiert': 'scheduled',
      'gewonnen': 'won',
      'verloren': 'lost',
      'prozess': 'in-progress'
    };
    
    for (const [keyword, status] of Object.entries(statusKeywords)) {
      if (text.toLowerCase().includes(keyword)) {
        leadData.status = status as LeadStatus;
        break;
      }
    }
    
    // Extract priority
    if (text.toLowerCase().includes('hoch') || text.toLowerCase().includes('wichtig')) {
      leadData.priority = 'high' as LeadPriority;
    } else if (text.toLowerCase().includes('niedrig')) {
      leadData.priority = 'low' as LeadPriority;
    } else {
      leadData.priority = 'medium' as LeadPriority;
    }
    
    // Extract notes
    const notesPatterns = [
      /(?:notiz|note|kommentar)[\s:]*(.+)/i,
      /(?:info|information)[\s:]*(.+)/i
    ];
    
    for (const pattern of notesPatterns) {
      const notesMatch = text.match(pattern);
      if (notesMatch) {
        leadData.notes = notesMatch[1].trim();
        break;
      }
    }
    
    // Only return if we have at least a firstName
    if (leadData.firstName) {
      return leadData;
    }
    
    return null;
  };

  const findLeadByIdentifier = async (identifier: string): Promise<Lead | null> => {
    try {
      const leads = await fetchLeads();
      
      // Filter to only include leads assigned to current user
      const userLeads = leads.filter(lead => lead.assignedTo === currentUser?.id);
      
      // Try to find by ID (if it's a number)
      if (/^\d+$/.test(identifier)) {
        const lead = userLeads.find(l => l.id === identifier);
        if (lead) return lead;
      }
      
      // Try to find by name
      const nameParts = identifier.toLowerCase().split(' ');
      const lead = userLeads.find(l => {
        const fullName = `${l.firstName} ${l.lastName}`.toLowerCase();
        return nameParts.every(part => fullName.includes(part));
      });
      
      return lead || null;
    } catch (error) {
      logger.error('Error finding lead:', error);
      return null;
    }
  };

  const findLeadsBySearchData = async (searchData: { firstName?: string; lastName?: string; phone?: string }): Promise<Lead[]> => {
    try {
      const leads = await fetchLeads();
      
      // Filter to only include leads assigned to current user
      const userLeads = leads.filter(lead => lead.assignedTo === currentUser?.id);
      
      return userLeads.filter(lead => {
        // Search by firstName
        if (searchData.firstName) {
          const firstNameMatch = lead.firstName?.toLowerCase().includes(searchData.firstName.toLowerCase());
          if (!firstNameMatch) return false;
        }
        
        // Search by lastName
        if (searchData.lastName) {
          const lastNameMatch = lead.lastName?.toLowerCase().includes(searchData.lastName.toLowerCase());
          if (!lastNameMatch) return false;
        }
        
        // Search by phone
        if (searchData.phone) {
          const cleanSearchPhone = searchData.phone.replace(/[^\d+]/g, '');
          const cleanLeadPhone = lead.phone?.replace(/[^\d+]/g, '') || '';
          const phoneMatch = cleanLeadPhone.includes(cleanSearchPhone) || cleanSearchPhone.includes(cleanLeadPhone);
          if (!phoneMatch) return false;
        }
        
        return true;
      });
    } catch (error) {
      logger.error('Error searching leads:', error);
      return [];
    }
  };

  const handleLeadSearchStep = async (message: string) => {
    const lowerMessage = message.toLowerCase();
    
    // Allow canceling at any time
    if (lowerMessage.includes('abbrechen') || lowerMessage.includes('stop')) {
      resetWorkflows();
      addMessage(`❌ Lead-Suche abgebrochen.`, 'bot');
      return;
    }
    
    switch (leadSearch.currentStep) {
      case 'firstName': {
        const firstName = message.trim();
        if (firstName) {
          setLeadSearch(prev => ({
            ...prev,
            currentStep: 'lastName',
            searchData: { ...prev.searchData, firstName }
          }));
          addMessage(`✅ Vorname: ${firstName}\n\nJetzt bitte den Nachnamen (oder "weiter" für Telefonnummer):`, 'bot');
        } else {
          addMessage(`❌ Bitte gib einen gültigen Vornamen ein.`, 'bot');
        }
        break;
      }
        
      case 'lastName': {
        if (lowerMessage === 'weiter' || lowerMessage === 'skip') {
          setLeadSearch(prev => ({
            ...prev,
            currentStep: 'phone'
          }));
          addMessage(`📞 Telefonnummer eingeben (oder "suchen" um jetzt zu suchen):`, 'bot');
        } else {
          const lastName = message.trim();
          if (lastName) {
            setLeadSearch(prev => ({
              ...prev,
              currentStep: 'phone',
              searchData: { ...prev.searchData, lastName }
            }));
            addMessage(`✅ Name: ${leadSearch.searchData.firstName} ${lastName}\n\n📞 Telefonnummer eingeben (oder "suchen" um jetzt zu suchen):`, 'bot');
          } else {
            addMessage(`❌ Bitte gib einen gültigen Nachnamen ein oder "weiter".`, 'bot');
          }
        }
        break;
      }
        
      case 'phone': {
        if (lowerMessage === 'suchen' || lowerMessage === 'fertig') {
          await performLeadSearch();
        } else {
          const phone = message.trim();
          if (phone) {
            setLeadSearch(prev => ({
              ...prev,
              searchData: { ...prev.searchData, phone }
            }));
            addMessage(`✅ Telefon: ${phone}\n\n🔍 Alle Daten erfasst. Suche wird gestartet...`, 'bot');
            await performLeadSearch();
          } else {
            addMessage(`❌ Bitte gib eine gültige Telefonnummer ein oder "suchen".`, 'bot');
          }
        }
        break;
      }
    }
  };

  const performLeadSearch = async () => {
    try {
      const results = await findLeadsBySearchData(leadSearch.searchData);
      
      if (results.length === 0) {
        addMessage(`❌ Keine deiner Leads gefunden.\n\nSuchkriterien:\n${leadSearch.searchData.firstName ? `• Vorname: ${leadSearch.searchData.firstName}` : ''}\n${leadSearch.searchData.lastName ? `• Nachname: ${leadSearch.searchData.lastName}` : ''}\n${leadSearch.searchData.phone ? `• Telefon: ${leadSearch.searchData.phone}` : ''}\n\n🔒 Suche nur in deinen zugewiesenen Leads.`, 'bot');
        // Reset search state when no results found
        setLeadSearch({ isSearching: false, currentStep: 'firstName', searchData: {} });
      } else if (results.length === 1) {
        const lead = results[0];
        // Reset only creation and search, keep lead edit
        setLeadCreation({ isCreating: false, currentStep: 'firstName', leadData: {} });
        setLeadSearch({ isSearching: false, currentStep: 'firstName', searchData: {} });
        // Set the lead edit state
        setLeadEdit({ 
          isEditing: true, 
          currentStep: 'action', 
          leadToEdit: lead, 
          editData: {} 
        });
        addMessage(`🎯 Lead gefunden:\n\n👤 **${lead.firstName} ${lead.lastName}**\n📧 ${lead.email || 'Keine Email'}\n📞 ${lead.phone || 'Kein Telefon'}\n🎯 Status: **${lead.status}**\n⭐ Priorität: ${lead.priority}\n📝 Notizen: ${lead.notes || 'Keine Notizen'}\n\n✨ **Verwende die Bearbeitungsbuttons unten!**`, 'bot', 'search', lead);
      } else {
        const leadList = results.slice(0, 5).map(lead => 
          `• ${lead.firstName} ${lead.lastName}${lead.phone ? ` (${lead.phone})` : ''}`
        ).join('\n');
        
        addMessage(`🔍 ${results.length} deiner Leads gefunden:\n\n${leadList}\n\n${results.length > 5 ? `... und ${results.length - 5} weitere\n` : ''}📊 Alle deine zugewiesenen Leads durchsucht.`, 'bot', 'search');
        // Reset search state when multiple results found
        setLeadSearch({ isSearching: false, currentStep: 'firstName', searchData: {} });
      }
    } catch (error) {
      logger.error('Error performing lead search:', error);
      addMessage(`❌ Fehler bei der Suche. Bitte versuche es erneut.`, 'bot');
      // Reset search state only on error
      setLeadSearch({ isSearching: false, currentStep: 'firstName', searchData: {} });
    }
  };

  const handleLeadEditStep = async (message: string) => {
    const lowerMessage = message.toLowerCase();
    
    // Allow canceling at any time
    if (lowerMessage.includes('abbrechen') || lowerMessage.includes('stop')) {
      resetWorkflows();
      addMessage(`❌ Lead-Bearbeitung abgebrochen.`, 'bot');
      return;
    }
    
    if (!leadEdit.leadToEdit) {
      resetWorkflows();
      addMessage(`❌ Fehler: Kein Lead ausgewählt.`, 'bot');
      return;
    }
    
    switch (leadEdit.currentStep) {
      case 'action': {
        // Action selection is now handled via buttons only, ignore text input
        addMessage(`💡 Verwende die **Bearbeitungsbuttons unten** um den Lead zu ändern:

🎯 **Status** - Status ändern
📝 **Notiz** - Notiz hinzufügen  
📞 **Kontakt** - Nächsten Kontakt setzen
✓ **Fertig** - Bearbeitung beenden`, 'bot');
        break;
      }
        
      case 'status': {
        const statusMap: Record<string, LeadStatus> = {
          'neu': 'new',
          'new': 'new',
          'kontaktiert': 'not-reached',
          'nicht erreicht': 'not-reached',
          'not-reached': 'not-reached',
          'terminiert': 'scheduled',
          'termin': 'scheduled',
          'scheduled': 'scheduled',
          'prozess': 'in-progress',
          'bearbeitung': 'in-progress',
          'in-progress': 'in-progress',
          'gewonnen': 'won',
          'won': 'won',
          'verloren': 'lost',
          'lost': 'lost'
        };
        
        const newStatus = statusMap[lowerMessage];
        if (newStatus) {
          try {
            logger.info('Attempting to update lead status:', { leadId: leadEdit.leadToEdit.id, newStatus, oldStatus: leadEdit.leadToEdit.status });
            
            const updateResult = await updateLead(leadEdit.leadToEdit.id, { 
              status: newStatus,
              lastUpdated: new Date().toISOString()
            }, leadEdit.leadToEdit);
            
            if (updateResult) {
              const statusLabel = STATUS_LABELS[newStatus] || newStatus;
              addMessage(`✅ Status erfolgreich geändert!

${leadEdit.leadToEdit.firstName} ${leadEdit.leadToEdit.lastName}
Status: **${leadEdit.leadToEdit.status}** → **${statusLabel}**

Lead wurde aktualisiert.`, 'bot');
              
              // Update the lead data and call callback with updated lead
              const updatedLead = { ...leadEdit.leadToEdit, status: newStatus, lastUpdated: new Date().toISOString() };
              onLeadUpdated?.(updatedLead);
              
              // Dispatch a custom event to notify other components
              window.dispatchEvent(new CustomEvent('leadUpdated', { detail: { lead: updatedLead } }));
              
              toast({
                title: "Status geändert",
                description: `${leadEdit.leadToEdit.firstName} ${leadEdit.leadToEdit.lastName} Status auf "${statusLabel}" gesetzt.`,
              });
            } else {
              logger.error('updateLead returned false - update failed');
              addMessage(`❌ Status konnte nicht geändert werden. Bitte versuche es erneut.`, 'bot');
            }
          } catch (error) {
            logger.error('Error updating lead status:', error);
            addMessage(`❌ Fehler beim Ändern des Status: ${error}`, 'bot');
          }
          resetWorkflows();
        } else {
          addMessage(`❌ Ungültiger Status: "${message}"

Verfügbare Status: neu, kontaktiert, terminiert, prozess, gewonnen, verloren`, 'bot');
        }
        break;
      }
        
      case 'notes': {
        if (message.trim()) {
          try {
            const updatedNotes = leadEdit.leadToEdit.notes 
              ? `${leadEdit.leadToEdit.notes}\n\n[${new Date().toLocaleDateString('de-DE')}] ${message.trim()}`
              : message.trim();
              
            await updateLead(leadEdit.leadToEdit.id, { 
              notes: updatedNotes,
              lastUpdated: new Date().toISOString()
            }, leadEdit.leadToEdit);
            
            addMessage(`✅ Notiz erfolgreich hinzugefügt!

${leadEdit.leadToEdit.firstName} ${leadEdit.leadToEdit.lastName}
Neue Notiz: "${message.trim()}"

Lead wurde aktualisiert.`, 'bot');
            
            // Update the lead data and call callback with updated lead
            const updatedLead = { ...leadEdit.leadToEdit, notes: updatedNotes, lastUpdated: new Date().toISOString() };
            onLeadUpdated?.(updatedLead);
            
            // Dispatch a custom event to notify other components
            window.dispatchEvent(new CustomEvent('leadUpdated', { detail: { lead: updatedLead } }));
            
            toast({
              title: "Notiz hinzugefügt",
              description: `Notiz zu ${leadEdit.leadToEdit.firstName} ${leadEdit.leadToEdit.lastName} hinzugefügt.`,
            });
          } catch (error) {
            logger.error('Error updating lead notes:', error);
            addMessage(`❌ Fehler beim Hinzufügen der Notiz. Bitte versuche es erneut.`, 'bot');
          }
          resetWorkflows();
        } else {
          addMessage(`❌ Bitte gib eine gültige Notiz ein.`, 'bot');
        }
        break;
      }
        
      case 'contact': {
        let contactDate: Date | null = null;
        
        if (lowerMessage === 'heute') {
          contactDate = new Date();
        } else if (lowerMessage === 'morgen') {
          contactDate = new Date();
          contactDate.setDate(contactDate.getDate() + 1);
        } else {
          // Try to parse date string
          const dateRegex = /(\d{1,2})\.(\d{1,2})\.?(\d{4})?/;
          const dateMatch = message.match(dateRegex);
          if (dateMatch) {
            const day = parseInt(dateMatch[1]);
            const month = parseInt(dateMatch[2]) - 1; // Month is 0-indexed
            const year = dateMatch[3] ? parseInt(dateMatch[3]) : new Date().getFullYear();
            contactDate = new Date(year, month, day);
          }
        }
        
        if (contactDate) {
          try {
            await updateLead(leadEdit.leadToEdit.id, { 
              nextContactDate: contactDate.toISOString(),
              lastUpdated: new Date().toISOString()
            }, leadEdit.leadToEdit);
            
            const formattedDate = contactDate.toLocaleDateString('de-DE');
            addMessage(`✅ Nächster Kontakt erfolgreich gesetzt!

${leadEdit.leadToEdit.firstName} ${leadEdit.leadToEdit.lastName}
Nächster Kontakt: **${formattedDate}**

Lead wurde aktualisiert.`, 'bot');
            
            // Update the lead data and call callback with updated lead
            const updatedLead = { ...leadEdit.leadToEdit, nextContactDate: contactDate.toISOString(), lastUpdated: new Date().toISOString() };
            onLeadUpdated?.(updatedLead);
            
            // Dispatch a custom event to notify other components
            window.dispatchEvent(new CustomEvent('leadUpdated', { detail: { lead: updatedLead } }));
            
            toast({
              title: "Kontakt gesetzt",
              description: `Nächster Kontakt für ${leadEdit.leadToEdit.firstName} ${leadEdit.leadToEdit.lastName} auf ${formattedDate} gesetzt.`,
            });
          } catch (error) {
            logger.error('Error updating lead contact date:', error);
            addMessage(`❌ Fehler beim Setzen des Kontakttermins. Bitte versuche es erneut.`, 'bot');
          }
          resetWorkflows();
        } else {
          addMessage(`❌ Ungültiges Datum: "${message}"

Verwende: "heute", "morgen" oder "DD.MM.YYYY"
Beispiele: "heute", "morgen", "15.12.2024"`, 'bot');
        }
        break;
      }
    }
  };

  const handleLeadCreationStep = async (message: string) => {
    const lowerMessage = message.toLowerCase();
    
    // Allow canceling at any time
    if (lowerMessage.includes('abbrechen') || lowerMessage.includes('stop')) {
      resetWorkflows();
      addMessage(`❌ Lead-Erstellung abgebrochen.`, 'bot');
      return;
    }
    
    switch (leadCreation.currentStep) {
      case 'firstName': {
        const firstName = message.trim();
        if (firstName) {
          setLeadCreation(prev => ({
            ...prev,
            currentStep: 'lastName',
            leadData: { ...prev.leadData, firstName }
          }));
          addMessage(`✅ Vorname: ${firstName}\n\nJetzt bitte den Nachnamen:`, 'bot');
        } else {
          addMessage(`❌ Bitte gib einen gültigen Vornamen ein.`, 'bot');
        }
        break;
      }
        
      case 'lastName': {
        const lastName = message.trim();
        if (lastName) {
          setLeadCreation(prev => ({
            ...prev,
            currentStep: 'notes',
            leadData: { ...prev.leadData, lastName }
          }));
          addMessage(`✅ Name: ${leadCreation.leadData.firstName} ${lastName}\n\n📝 Möchtest du eine Notiz hinzufügen?\n\nGib eine kurze Notiz ein oder schreibe "fertig" um den Lead zu erstellen:`, 'bot');
        } else {
          addMessage(`❌ Bitte gib einen gültigen Nachnamen ein.`, 'bot');
        }
        break;
      }
        
      case 'notes': {
        const finalLeadData = { ...leadCreation.leadData };
        
        if (lowerMessage !== 'fertig' && lowerMessage !== 'skip' && message.trim()) {
          finalLeadData.notes = message.trim();
        }
        
        // Create the lead
        const newLeadData = {
          firstName: finalLeadData.firstName || '',
          lastName: finalLeadData.lastName || '',
          email: finalLeadData.email || '',
          phone: '',
          position: '',
          assignedTo: currentUser?.id || '',
          status: 'new',
          priority: 'medium',
          notes: finalLeadData.notes || '',
          attachment: '',
          nextContactDate: '',
          lastContactDate: '',
          dataProtectionConsent: true,
          marketplacePurchaseInfo: '',
          history: [{
            type: 'created' as const,
            date: new Date().toISOString(),
            user: currentUser?.name || 'System',
            changes: []
          }]
        };
        
        try {
          const createdLead = await createLead(newLeadData);
          
          addMessage(`✅ Lead erfolgreich erstellt!

👤 ${createdLead.firstName} ${createdLead.lastName}
📧 Email: ${createdLead.email || 'Nicht angegeben'}
📞 Telefon: ${createdLead.phone || 'Nicht angegeben'}
🎯 Status: ${createdLead.status}
⭐ Priorität: ${createdLead.priority}
👤 Zugewiesen an: ${currentUser?.name || 'Dir'}
📝 Notizen: ${createdLead.notes || 'Keine'}

Der Lead wurde dem System hinzugefügt und dir zugewiesen.`, 'bot', 'create', createdLead);
          
          onLeadCreated?.(createdLead);
          
          toast({
            title: "Lead erstellt",
            description: `${createdLead.firstName} ${createdLead.lastName} wurde erfolgreich erstellt und dir zugewiesen.`,
          });
        } catch (error) {
          logger.error('Error creating lead:', error);
          addMessage(`❌ Fehler beim Erstellen des Leads. Bitte versuche es erneut.`, 'bot');
        }
        
        // Reset creation state
        resetWorkflows();
        break;
      }
    }
  };

  const processMessage = async (message: string) => {
    setIsProcessing(true);
    const lowerMessage = message.toLowerCase();
    
    try {
      // Handle interactive lead creation flow
      if (leadCreation.isCreating) {
        await handleLeadCreationStep(message);
        return;
      }
      
      // Handle interactive lead search flow
      if (leadSearch.isSearching) {
        await handleLeadSearchStep(message);
        return;
      }
      
      // Handle interactive lead edit flow
      if (leadEdit.isEditing) {
        // Only handle text input for specific steps, not the action selection
        if (leadEdit.currentStep !== 'action') {
          await handleLeadEditStep(message);
          return;
        }
      }
      // Calendar command
      if (lowerMessage.includes('kalender') || lowerMessage.includes('heute') || lowerMessage.includes('termine')) {
        const allLeads = await fetchLeads();
        const userLeads = allLeads.filter(lead => lead.assignedTo === currentUser?.id);
        const today = new Date();
        const todayStr = today.toDateString();
        
        // Find leads with next contact date today
        const todayContacts = userLeads.filter(lead => {
          if (!lead.nextContactDate) return false;
          const contactDate = new Date(lead.nextContactDate);
          return contactDate.toDateString() === todayStr;
        });
        
        // Find overdue contacts
        const overdueContacts = userLeads.filter(lead => {
          if (!lead.nextContactDate) return false;
          const contactDate = new Date(lead.nextContactDate);
          return contactDate < today && contactDate.toDateString() !== todayStr;
        });
        
        // Find upcoming contacts (next 7 days)
        const nextWeek = new Date();
        nextWeek.setDate(today.getDate() + 7);
        const upcomingContacts = userLeads.filter(lead => {
          if (!lead.nextContactDate) return false;
          const contactDate = new Date(lead.nextContactDate);
          return contactDate > today && contactDate <= nextWeek;
        });
        
        let calendarMessage = `📅 Kalender-Übersicht:\n\n`;
        
        if (todayContacts.length > 0) {
          calendarMessage += `🔴 HEUTE zu kontaktieren (${todayContacts.length}):\n`;
          todayContacts.forEach(lead => {
            const time = lead.nextContactDate ? new Date(lead.nextContactDate).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '';
            calendarMessage += `• ${lead.firstName} ${lead.lastName} ${time ? `um ${time}` : ''}\n`;
          });
          calendarMessage += `\n`;
        }
        
        if (overdueContacts.length > 0) {
          calendarMessage += `⚠️ ÜBERFÄLLIG (${overdueContacts.length}):\n`;
          overdueContacts.slice(0, 3).forEach(lead => {
            const daysAgo = Math.floor((today.getTime() - new Date(lead.nextContactDate!).getTime()) / (1000 * 60 * 60 * 24));
            calendarMessage += `• ${lead.firstName} ${lead.lastName} (${daysAgo} Tage überfällig)\n`;
          });
          if (overdueContacts.length > 3) {
            calendarMessage += `... und ${overdueContacts.length - 3} weitere\n`;
          }
          calendarMessage += `\n`;
        }
        
        if (upcomingContacts.length > 0) {
          calendarMessage += `📋 DIESE WOCHE (${upcomingContacts.length}):\n`;
          upcomingContacts.slice(0, 5).forEach(lead => {
            const date = new Date(lead.nextContactDate!).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
            calendarMessage += `• ${lead.firstName} ${lead.lastName} (${date})\n`;
          });
          if (upcomingContacts.length > 5) {
            calendarMessage += `... und ${upcomingContacts.length - 5} weitere\n`;
          }
        }
        
        if (todayContacts.length === 0 && overdueContacts.length === 0 && upcomingContacts.length === 0) {
          calendarMessage += `✅ Keine anstehenden Kontakte in den nächsten 7 Tagen!\n\n💡 Tipp: Setze Kontaktdaten mit "Setze nächsten Kontakt für [Name] auf [Datum]"`;
        }
        
        addMessage(calendarMessage, 'bot');
        return;
      }

      // Help command
      if (lowerMessage.includes('hilfe') || lowerMessage.includes('help') || lowerMessage.includes('kommandos')) {
        addMessage(`🤖 Verfügbare Kommandos:

**Schnellaktionen verwenden:**
• Klicke unten auf "Erstellen", "Suchen", "Kalender" oder "Statistik"

**Lead erstellen:**
• Schritt-für-Schritt Dialog führt dich durch die Erstellung

**Lead suchen:**
• Nach Vorname, Nachname oder Telefonnummer suchen

**Textkommandos:**
• "Ändere Status von [Name] zu terminiert"
• "Füge Notiz zu [Name] hinzu: [Text]"
• "Setze nächsten Kontakt für [Name] auf morgen"

**Tipps:**
• Du kannst jederzeit "abbrechen" eingeben
• Verwende die Schnellaktionen für beste Bedienung
• Bei Problemen einfach "Hilfe" schreiben

Probiere die Buttons unten aus! 😊`, 'bot', 'help');
        return;
      }
      
      // Create lead command
      if (lowerMessage.includes('erstelle') || lowerMessage.includes('neuer lead') || lowerMessage.includes('lead erstellen')) {
        const leadData = parseLeadFromText(message);
        
        if (leadData && leadData.firstName) {
          // Quick creation if full name is provided
          if (!leadData.lastName) {
            // Start interactive process
            setLeadCreation({ 
              isCreating: true, 
              currentStep: 'lastName', 
              leadData: { firstName: leadData.firstName } 
            });
            addMessage(`📝 Ich erstelle einen Lead für "${leadData.firstName}".

Bitte gib den Nachnamen an:`, 'bot');
            return;
          }
          
          // Set defaults for required fields with proper structure
          const newLeadData = {
            firstName: leadData.firstName,
            lastName: leadData.lastName,
            email: leadData.email || '',
            phone: leadData.phone || '',
            position: '',
            assignedTo: currentUser?.id || '',
            status: leadData.status || 'new',
            priority: leadData.priority || 'medium',
            notes: leadData.notes || '',
            attachment: '',
            nextContactDate: '',
            lastContactDate: '',
            dataProtectionConsent: true,
            marketplacePurchaseInfo: '',
            history: [{
              type: 'created' as const,
              date: new Date().toISOString(),
              user: currentUser?.name || 'System',
              changes: []
            }]
          };
          
          logger.log('Creating lead with data:', newLeadData);
          
          try {
            const createdLead = await createLead(newLeadData);
            
            addMessage(`✅ Lead erfolgreich erstellt!

👤 ${createdLead.firstName} ${createdLead.lastName}
📧 Email: ${createdLead.email || 'Nicht angegeben'}
📞 Telefon: ${createdLead.phone || 'Nicht angegeben'}
🎯 Status: ${createdLead.status}
⭐ Priorität: ${createdLead.priority}
👤 Zugewiesen an: ${currentUser?.name || 'Dir'}
📝 Notizen: ${createdLead.notes || 'Keine'}

Der Lead wurde dem System hinzugefügt und dir zugewiesen.`, 'bot', 'create', createdLead);
            
            onLeadCreated?.(createdLead);
            
            toast({
              title: "Lead erstellt",
              description: `${createdLead.firstName} ${createdLead.lastName} wurde erfolgreich erstellt und dir zugewiesen.`,
            });
          } catch (error) {
            logger.error('Error creating lead:', error);
            addMessage(`❌ Fehler beim Erstellen des Leads. Bitte versuche es erneut.`, 'bot');
          }
          
        } else {
          // Start interactive lead creation
          setLeadCreation({ isCreating: true, currentStep: 'firstName', leadData: {} });
          addMessage(`📝 Lead-Assistent bereit!

Ich helfe dir beim Erstellen eines neuen Leads.
Bitte gib den Vornamen ein:

(Du kannst jederzeit "abbrechen" eingeben, um zu stoppen)`, 'bot');
        }
        return;
      }
      
      // Statistics command
      if (lowerMessage.includes('statistik') || lowerMessage.includes('zahlen') || lowerMessage.includes('übersicht')) {
        const allLeads = await fetchLeads();
        
        // Filter to show only user's assigned leads
        const userLeads = allLeads.filter(lead => lead.assignedTo === currentUser?.id);
        const today = new Date().toDateString();
        
        const todayLeads = userLeads.filter(lead => 
          lead.createdAt && new Date(lead.createdAt).toDateString() === today
        );
        
        const statusCounts = userLeads.reduce((acc, lead) => {
          const status = lead.status || 'unknown';
          const statusLabel = status === 'new' ? 'neu' : 
                             status === 'not-reached' ? 'kontaktiert' :
                             status === 'scheduled' ? 'terminiert' :
                             status === 'won' ? 'gewonnen' :
                             status === 'lost' ? 'verloren' :
                             status === 'in-progress' ? 'in Bearbeitung' : status;
          acc[statusLabel] = (acc[statusLabel] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);
        
        addMessage(`📊 Deine Lead-Statistik:

📅 Heute: ${todayLeads.length} neue Leads
📈 Deine Leads gesamt: ${userLeads.length}

Status-Verteilung:
${Object.entries(statusCounts).map(([status, count]) => 
  `• ${status}: ${count}`
).join('\n')}

${userLeads.length === 0 ? '💡 Du hast noch keine Leads zugewiesen bekommen.' : ''}`, 'bot');
        return;
      }
      
      // Default response for unrecognized commands
      addMessage(`🤔 Das habe ich nicht ganz verstanden. 

Verwende am besten die **Schnellaktionen** unten:
• 🆕 **Erstellen** - Neuen Lead anlegen
• 🔍 **Suchen** - Lead finden  
• 📅 **Kalender** - Termine anzeigen
• 📊 **Statistik** - Deine Zahlen

Oder beschreibe, was du machen möchtest! 😊`, 'bot');
      
    } catch (error) {
      logger.error('Error processing chatbot message:', error);
      addMessage(`❌ Ein Fehler ist aufgetreten. Bitte versuche es erneut.`, 'bot');
      
      toast({
        title: "Chatbot Fehler",
        description: "Beim Verarbeiten deiner Nachricht ist ein Fehler aufgetreten.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isProcessing) return;
    
    const message = inputValue.trim();
    setInputValue('');
    
    // Add user message
    addMessage(message, 'user');
    
    // Process the message
    await processMessage(message);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const startLeadCreation = () => {
    resetWorkflows();
    setLeadCreation({ isCreating: true, currentStep: 'firstName', leadData: {} });
    addMessage(`📝 Neuen Lead erstellen

Bitte gib den Vornamen ein:

(Du kannst jederzeit "abbrechen" eingeben, um zu stoppen)`, 'bot');
  };

  const startLeadSearch = () => {
    resetWorkflows();
    setLeadSearch({ isSearching: true, currentStep: 'firstName', searchData: {} });
    addMessage(`🔍 Lead-Suche starten

Bitte gib den Vornamen ein:

(Du kannst jederzeit "abbrechen" eingeben, um zu stoppen)`, 'bot');
  };

  const showCalendar = () => {
    addMessage("Kalender", 'user');
    processMessage("Kalender");
  };

  const showStatistics = () => {
    addMessage("Statistik", 'user');
    processMessage("Statistik");
  };

  const showHelp = () => {
    addMessage("Hilfe", 'user');
    processMessage("Hilfe");
  };

  return (
    <>
      {/* Floating Action Button */}
      {!isOpen && (
        <Button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 z-50"
          size="icon"
        >
          <MessageCircle className="h-6 w-6" />
        </Button>
      )}

      {/* Chatbot Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-96 h-[600px] shadow-xl z-50 flex flex-col bg-background border rounded-lg overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b bg-background">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
                <Bot className="h-4 w-4 text-primary-foreground" />
              </div>
              <h3 className="text-sm font-semibold">Lead-Assistent</h3>
              <Badge variant="secondary" className="text-xs">
                Online
              </Badge>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(false)}
              className="h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Workflow Status Bar - Show during active workflows */}
          {(leadCreation.isCreating || leadSearch.isSearching || leadEdit.isEditing) && (
            <div className={cn(
              "border-b p-3",
              leadCreation.isCreating ? "bg-green-50" : 
              leadSearch.isSearching ? "bg-blue-50" : "bg-orange-50"
            )}>
              <div className="flex items-center justify-between mb-2">
                <div className={cn(
                  "text-xs font-medium",
                  leadCreation.isCreating ? "text-green-700" : 
                  leadSearch.isSearching ? "text-blue-700" : "text-orange-700"
                )}>
                  {leadCreation.isCreating ? (
                    <>
                      📝 Lead erstellen - 
                      {leadCreation.currentStep === 'firstName' && 'Schritt 1/3: Vorname'}
                      {leadCreation.currentStep === 'lastName' && 'Schritt 2/3: Nachname'}
                      {leadCreation.currentStep === 'notes' && 'Schritt 3/3: Notiz (optional)'}
                    </>
                  ) : leadSearch.isSearching ? (
                    <>
                      🔍 Lead suchen - 
                      {leadSearch.currentStep === 'firstName' && 'Schritt 1/3: Vorname'}
                      {leadSearch.currentStep === 'lastName' && 'Schritt 2/3: Nachname (optional)'}
                      {leadSearch.currentStep === 'phone' && 'Schritt 3/3: Telefonnummer (optional)'}
                    </>
                  ) : (
                    <>
                      ✏️ Lead bearbeiten - 
                      {leadEdit.currentStep === 'action' && 'Aktion wählen'}
                      {leadEdit.currentStep === 'status' && 'Status ändern'}
                      {leadEdit.currentStep === 'notes' && 'Notiz hinzufügen'}
                      {leadEdit.currentStep === 'contact' && 'Kontakt setzen'}
                    </>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    resetWorkflows();
                    addMessage(`❌ ${leadCreation.isCreating ? 'Lead-Erstellung' : 
                                 leadSearch.isSearching ? 'Lead-Suche' : 'Lead-Bearbeitung'} abgebrochen.`, 'bot');
                  }}
                  className="text-xs h-6 px-2"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
              <div className="flex gap-2">
                {leadCreation.isCreating && leadCreation.currentStep === 'notes' && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      handleLeadCreationStep('fertig');
                    }}
                    className="text-xs h-6"
                  >
                    ✅ Ohne Notiz erstellen
                  </Button>
                )}
                {leadSearch.isSearching && (leadSearch.currentStep === 'lastName' || leadSearch.currentStep === 'phone') && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      if (leadSearch.currentStep === 'lastName') {
                        handleLeadSearchStep('weiter');
                      } else {
                        handleLeadSearchStep('suchen');
                      }
                    }}
                    className="text-xs h-6"
                  >
                    {leadSearch.currentStep === 'lastName' ? '⏭️ Überspringen' : '🔍 Jetzt suchen'}
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Messages Area */}
          <div className="flex-1 overflow-hidden">
            <ScrollArea className="h-full">
              <div className="p-4 space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={cn(
                      "flex gap-2",
                      message.type === 'user' ? "justify-end" : "justify-start"
                    )}
                  >
                    {message.type === 'bot' && (
                      <div className="h-6 w-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0 mt-1">
                        <Bot className="h-3 w-3 text-primary-foreground" />
                      </div>
                    )}
                    <div
                      className={cn(
                        "max-w-[75%] rounded-lg px-3 py-2 text-sm break-words",
                        message.type === 'user'
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted"
                      )}
                    >
                      <div className="whitespace-pre-wrap">{message.content}</div>
                      <div className="text-xs opacity-60 mt-1">
                        {message.timestamp.toLocaleTimeString([], { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </div>
                    </div>
                    {message.type === 'user' && (
                      <div className="h-6 w-6 rounded-full bg-secondary flex items-center justify-center flex-shrink-0 mt-1">
                        <User className="h-3 w-3" />
                      </div>
                    )}
                  </div>
                ))}
                {isProcessing && (
                  <div className="flex gap-2 justify-start">
                    <div className="h-6 w-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0 mt-1">
                      <Bot className="h-3 w-3 text-primary-foreground" />
                    </div>
                    <div className="bg-muted rounded-lg px-3 py-2 text-sm">
                      <div className="flex gap-1">
                        <div className="h-2 w-2 bg-primary/60 rounded-full animate-bounce"></div>
                        <div className="h-2 w-2 bg-primary/60 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                        <div className="h-2 w-2 bg-primary/60 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>
          </div>

          {/* Input Area */}
          <div className="border-t p-4 bg-background">
            <div className="flex gap-2 mb-3">
              <Input
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder={
                  leadCreation.isCreating 
                    ? leadCreation.currentStep === 'firstName' ? 'Vorname eingeben...'
                    : leadCreation.currentStep === 'lastName' ? 'Nachname eingeben...'
                    : leadCreation.currentStep === 'notes' ? 'Notiz eingeben oder "fertig"...'
                    : 'Schreibe eine Nachricht...'
                  : leadSearch.isSearching
                    ? leadSearch.currentStep === 'firstName' ? 'Vorname eingeben...'
                    : leadSearch.currentStep === 'lastName' ? 'Nachname eingeben oder "weiter"...'
                    : leadSearch.currentStep === 'phone' ? 'Telefonnummer eingeben oder "suchen"...'
                    : 'Schreibe eine Nachricht...'
                  : leadEdit.isEditing
                    ? leadEdit.currentStep === 'action' ? 'Verwende die Buttons unten zum Bearbeiten...'
                    : leadEdit.currentStep === 'status' ? 'Wähle Status mit den Buttons unten...'
                    : leadEdit.currentStep === 'notes' ? 'Notiz eingeben...'
                    : leadEdit.currentStep === 'contact' ? 'Datum eingeben (heute, morgen, DD.MM)...'
                    : 'Schreibe eine Nachricht...'
                    : 'Schreibe eine Nachricht...'
                }
                disabled={isProcessing}
                className="flex-1"
              />
              <Button
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isProcessing}
                size="icon"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Dynamic Quick Actions */}
            <div className="border-t pt-3">
              <div className="text-xs text-muted-foreground mb-2 font-medium">
                {leadEdit.isEditing && leadEdit.currentStep === 'action' ? 'Lead bearbeiten:' : 'Schnellaktionen:'}
              </div>
              <div className="grid grid-cols-2 gap-2 mb-2">
                {leadEdit.isEditing && leadEdit.currentStep === 'action' ? (
                  // Lead Edit Buttons
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setLeadEdit(prev => ({ ...prev, currentStep: 'status' }));
                        addMessage(`🎯 Status ändern für ${leadEdit.leadToEdit?.firstName} ${leadEdit.leadToEdit?.lastName}\n\nAktueller Status: **${leadEdit.leadToEdit?.status}**\n\nWähle den neuen Status:`, 'bot');
                      }}
                      className="text-xs h-7 justify-start bg-orange-50 hover:bg-orange-100 border-orange-200"
                      disabled={isProcessing}
                    >
                      🎯 Status
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setLeadEdit(prev => ({ ...prev, currentStep: 'notes' }));
                        addMessage(`📝 Notiz hinzufügen für ${leadEdit.leadToEdit?.firstName} ${leadEdit.leadToEdit?.lastName}\n\nAktuelle Notizen:\n${leadEdit.leadToEdit?.notes || 'Keine Notizen vorhanden'}\n\nGib deine neue Notiz ein:`, 'bot');
                      }}
                      className="text-xs h-7 justify-start bg-orange-50 hover:bg-orange-100 border-orange-200"
                      disabled={isProcessing}
                    >
                      📝 Notiz
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setLeadEdit(prev => ({ ...prev, currentStep: 'contact' }));
                        addMessage(`📞 Nächsten Kontakt setzen für ${leadEdit.leadToEdit?.firstName} ${leadEdit.leadToEdit?.lastName}\n\nGib das Datum ein:\n• **heute** - Heute kontaktieren\n• **morgen** - Morgen kontaktieren\n• **DD.MM.YYYY** - Bestimmtes Datum\n\nBeispiele: "heute", "morgen", "15.12.2024"`, 'bot');
                      }}
                      className="text-xs h-7 justify-start bg-orange-50 hover:bg-orange-100 border-orange-200"
                      disabled={isProcessing}
                    >
                      📞 Kontakt
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        resetWorkflows();
                        addMessage(`✨ Lead-Bearbeitung beendet. Zurück zum Hauptmenü.`, 'bot');
                      }}
                      className="text-xs h-7 justify-start"
                      disabled={isProcessing}
                    >
                      ✓ Fertig
                    </Button>
                  </>
                ) : leadEdit.isEditing && leadEdit.currentStep === 'status' ? (
                  // Status Selection Buttons
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleLeadEditStep('neu')}
                      className="text-xs h-7 justify-start bg-green-50 hover:bg-green-100 border-green-200"
                      disabled={isProcessing}
                    >
                      🆕 Neu
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleLeadEditStep('kontaktiert')}
                      className="text-xs h-7 justify-start bg-blue-50 hover:bg-blue-100 border-blue-200"
                      disabled={isProcessing}
                    >
                      📞 Kontaktiert
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleLeadEditStep('terminiert')}
                      className="text-xs h-7 justify-start bg-purple-50 hover:bg-purple-100 border-purple-200"
                      disabled={isProcessing}
                    >
                      📅 Terminiert
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleLeadEditStep('prozess')}
                      className="text-xs h-7 justify-start bg-yellow-50 hover:bg-yellow-100 border-yellow-200"
                      disabled={isProcessing}
                    >
                      ⏳ In Prozess
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleLeadEditStep('gewonnen')}
                      className="text-xs h-7 justify-start bg-green-50 hover:bg-green-100 border-green-200"
                      disabled={isProcessing}
                    >
                      🎆 Gewonnen
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleLeadEditStep('verloren')}
                      className="text-xs h-7 justify-start bg-red-50 hover:bg-red-100 border-red-200"
                      disabled={isProcessing}
                    >
                      ❌ Verloren
                    </Button>
                  </>
                ) : (
                  // Normal Quick Actions
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={startLeadCreation}
                      className="text-xs h-7 justify-start"
                      disabled={isProcessing}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Erstellen
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={startLeadSearch}
                      className="text-xs h-7 justify-start"
                      disabled={isProcessing}
                    >
                      <Search className="h-3 w-3 mr-1" />
                      Suchen
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={showCalendar}
                      className="text-xs h-7 justify-start"
                      disabled={isProcessing}
                    >
                      📅 Kalender
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={showStatistics}
                      className="text-xs h-7 justify-start"
                      disabled={isProcessing}
                    >
                      📊 Statistik
                    </Button>
                  </>
                )}
              </div>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={showHelp}
                  className="text-xs h-6 px-2 flex-1"
                  disabled={isProcessing}
                >
                  <Lightbulb className="h-3 w-3 mr-1" />
                  Hilfe
                </Button>
                {(leadCreation.isCreating || leadSearch.isSearching || leadEdit.isEditing) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      resetWorkflows();
                      addMessage(`✨ Zurück zum Hauptmenü`, 'bot');
                    }}
                    className="text-xs h-6 px-2"
                  >
                    ← Zurück
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}